import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./contexts/AuthContext";
import { useDashboardCurrency } from "./hooks/useDashboardCurrency";
import { useMarketDataRefreshInterval } from "./hooks/useMarketDataRefreshInterval";
import { useDashboardLayout } from "./contexts/DashboardLayoutContext";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { DEFAULT_DASHBOARD_ORDER, DASHBOARD_WIDGETS, getBaseWidgetId, type WidgetInstanceId } from "./config/dashboardWidgets";
import EditableDashboardWidgets from "./components/EditableDashboardWidgets";
import { supabase } from "./lib/supabase";
import { handleScrollGuard, scrollContainerProps } from "./utils/scrollGuard";
import Login from "./components/Login";
import Register from "./components/Register";
import LanguagePreferenceModal from "./components/LanguagePreferenceModal";
import AddTrade, { AddTradeTrade } from "./components/AddTrade";
import CreateStrategy from "./components/CreateStrategy";
import TradeList from "./components/TradeList";
import DashboardWidgetOpenPositions from "./components/DashboardWidgetOpenPositions";
import DashboardWidgetTotalPnL from "./components/DashboardWidgetTotalPnL";
import DashboardWidgetTotalPnlOpen from "./components/DashboardWidgetTotalPnlOpen";
import DashboardWidgetTotalRisk from "./components/DashboardWidgetTotalRisk";
import DashboardWidgetMetric from "./components/DashboardWidgetMetric";
import DashboardFeed from "./components/DashboardFeed";
import AllTrades from "./AllTrades";
import Analytics from "./Analytics";
import Insights from "./Insights";
import Tools from "./Tools";
import Strategies from "./Strategies";
import Account from "./Account";
import SocialTrade from "./SocialTrade";
import TradePools from "./TradePools.tsx";
import Followers from "./Followers.tsx";
import Community from "./Community";
import Accounts from "./Accounts";
import Footer from "./components/Footer";
import DashboardLayout from "./components/DashboardLayout";
import QuickAddTradeModal from "./components/QuickAddTradeModal";

function App() {
  const { t, i18n } = useTranslation(['common', 'tools', 'pages']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, profileLoading, refreshProfile, loading: authLoading } = useAuth();
  const {
    enabled,
    order,
    isEditMode,
    pendingEnabled,
    pendingOrder,
    removeWidget,
    reorderWidgets,
    getWidgetSettings,
  } = useDashboardLayout();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const showLanguagePreferenceModal = Boolean(
    user && !profileLoading && userProfile && !userProfile.preferred_language
  );

  // Apply saved language from profile when profile loads
  useEffect(() => {
    if (userProfile?.preferred_language && userProfile.preferred_language !== i18n.language) {
      i18n.changeLanguage(userProfile.preferred_language);
    }
  }, [userProfile?.preferred_language, i18n]);

  const [currentPage, setCurrentPage] = useState<
    | "home"
    | "changelog"
    | "privacy-policy"
    | "terms-and-conditions"
    | "risk-warning"
    | "documentation"
    | "pricing"
    | "all-trades"
    | "insights"
    | "add-trade"
    | "edit-trade"
    | "create-strategy"
    | "analytics"
    | "accounts"
    | "tools"
    | "strategies"
    | "social-trade"
    | "social-trade-trade-pools"
    | "social-trade-followers"
    | "account"
    | "community"
  >("home");
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [tradeListRefresh, setTradeListRefresh] = useState(0);
  const [quickAddTradeOpen, setQuickAddTradeOpen] = useState(false);
  const [trades, setTrades] = useState<any[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [editTradeData, setEditTradeData] = useState<{ trade: AddTradeTrade | null; error: string | null }>({ trade: null, error: null });
  const [editTradeLoading, setEditTradeLoading] = useState(false);
  const [strategies, setStrategies] = useState<{ id: string; display_name: string }[]>([]);
  const [dashboardAccounts, setDashboardAccounts] = useState<{ id: string; name: string; platform: string; currency: string; initial_balance?: number | null }[]>([]);
  const { selectedCurrency, exchangeRates, latestRates, fetchExchangeRates, fetchLatestExchangeRates } = useDashboardCurrency(
    userProfile?.preferred_currency
  );

  // Risk/Reward & Position Size State
  const [entryPrice, setEntryPrice] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [target1, setTarget1] = useState("");
  const [target2, setTarget2] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [riskPercent, setRiskPercent] = useState("1");
  const [rrResult, setRrResult] = useState<{
    rr1: number | null;
    rr2: number | null;
    suggestedUnits: number | null;
    riskDollar: number | null;
    unitRiskDollar: number | null;
  }>({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });

  // Win Rate Calculator State
  const [winningTrades, setWinningTrades] = useState("");
  const [losingTrades, setLosingTrades] = useState("");
  const [winRate, setWinRate] = useState<number | null>(null);

  // Profit Factor Calculator State
  const [totalProfit, setTotalProfit] = useState("");
  const [totalLoss, setTotalLoss] = useState("");
  const [profitFactor, setProfitFactor] = useState<number | null>(null);

  // URL-based routing: determine page from URL path (/app/*)
  useEffect(() => {
    const path = location.pathname;
    if (path === '/app/all-trades') {
      setCurrentPage('all-trades');
    } else if (path === '/app/insights') {
      setCurrentPage('insights');
    } else if (path === '/app/analytics') {
      setCurrentPage('analytics');
    } else if (path === '/app/accounts') {
      setCurrentPage('accounts');
    } else if (path === '/app/tools') {
      setCurrentPage('tools');
    } else if (path === '/app/strategies') {
      setCurrentPage('strategies');
    } else if (path === '/app/social-trade/trade-pools') {
      setCurrentPage('social-trade-trade-pools');
    } else if (path === '/app/social-trade/followers') {
      setCurrentPage('social-trade-followers');
    } else if (path === '/app/social-trade') {
      setCurrentPage('social-trade');
    } else if (path === '/app/account') {
      setCurrentPage('account');
    } else if (path === '/app/community') {
      setCurrentPage('community');
    } else if (path === '/app/add-trade') {
      setCurrentPage('add-trade');
    } else if (path === '/app/create-strategy') {
      setCurrentPage('create-strategy');
    } else if (path.startsWith('/app/edit-trade/')) {
      setCurrentPage('edit-trade');
    } else if (path === '/app' || path === '/app/') {
      setCurrentPage('home');
    } else {
      if (currentPage !== 'home' && path.startsWith('/app')) {
        setCurrentPage('home');
      }
    }
  }, [location.pathname, location.search]);

  // If Supabase redirects back with an auth error, forward to reset page
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''))
      const errorCode = url.searchParams.get('error_code') || hashParams.get('error_code')
      const errorDescription =
        url.searchParams.get('error_description') || hashParams.get('error_description')
      if (errorCode) {
        const qp = new URLSearchParams()
        qp.set('error_code', errorCode)
        if (errorDescription) qp.set('error_description', errorDescription)
        navigate(`/reset-password?${qp.toString()}`, { replace: true })
      }
    } catch {
      // ignore
    }
  }, []);

  // Redirect to landing when on /app and not logged in
  useEffect(() => {
    if (!authLoading && location.pathname.startsWith('/app') && !user) {
      navigate("/?login=1", { replace: true });
    }
  }, [authLoading, location.pathname, user, navigate]);

  // Fetch trade when on edit-trade page
  const editTradeId = location.pathname.match(/^\/app\/edit-trade\/([^/]+)/)?.[1] ?? null;
  useEffect(() => {
    if (currentPage !== 'edit-trade' || !editTradeId || !user) {
      setEditTradeData({ trade: null, error: null });
      return;
    }
    let cancelled = false;
    setEditTradeLoading(true);
    setEditTradeData({ trade: null, error: null });
    (async () => {
      try {
        const { data, error } = await supabase
          .from('trades_with_market_data')
          .select('*')
          .eq('id', editTradeId)
          .eq('user_id', user.id)
          .single();
        if (cancelled) return;
        setEditTradeLoading(false);
        if (error || !data) {
          setEditTradeData({ trade: null, error: error?.message ?? 'Trade not found' });
          return;
        }
        setEditTradeData({ trade: data as AddTradeTrade, error: null });
      } catch (err: unknown) {
        if (!cancelled) {
          setEditTradeLoading(false);
          setEditTradeData({ trade: null, error: (err as Error)?.message ?? 'Failed to load trade' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentPage, editTradeId, user?.id]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage, location.pathname]);

  // Calculate Risk/Reward & Position Size automatically
  useEffect(() => {
    const entry = parseFloat(entryPrice);
    const stop = parseFloat(stopLoss);
    const tp1 = parseFloat(target1);
    const tp2 = parseFloat(target2);
    const account = parseFloat(accountSize);
    const risk = parseFloat(riskPercent);

    // Only calculate if we have minimum required values
    if (!entry || !stop || !account || !risk || risk <= 0 || account <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
      return;
    }

    if (entry <= 0 || stop <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
      return;
    }

    // Determine if long or short position
    const isLong = entry > stop;
    let riskAmount: number;
    let reward1: number | null = null;
    let reward2: number | null = null;

    if (isLong) {
      // Long position: stop < entry < targets
      if (stop >= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
        return;
      }
      riskAmount = entry - stop;
      if (tp1 && tp1 > entry) {
        reward1 = tp1 - entry;
      }
      if (tp2 && tp2 > entry) {
        reward2 = tp2 - entry;
      }
    } else {
      // Short position: stop > entry > targets
      if (stop <= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
        return;
      }
      riskAmount = stop - entry;
      if (tp1 && tp1 < entry) {
        reward1 = entry - tp1;
      }
      if (tp2 && tp2 < entry) {
        reward2 = entry - tp2;
      }
    }

    // Calculate results
    const riskDollar = (account * risk) / 100;
    const suggestedUnits = riskAmount > 0 ? riskDollar / riskAmount : null;
    const unitRiskDollar = riskAmount;

    const rr1 = reward1 !== null && riskAmount > 0 ? reward1 / riskAmount : null;
    const rr2 = reward2 !== null && riskAmount > 0 ? reward2 / riskAmount : null;

    setRrResult({
      rr1,
      rr2,
      suggestedUnits,
      riskDollar,
      unitRiskDollar,
    });
  }, [entryPrice, stopLoss, target1, target2, accountSize, riskPercent]);

  // Calculate Win Rate automatically
  useEffect(() => {
    const wins = parseFloat(winningTrades);
    const losses = parseFloat(losingTrades);

    if (wins >= 0 && losses >= 0 && (wins > 0 || losses > 0)) {
      const totalTrades = wins + losses;
      if (totalTrades > 0) {
        const rate = (wins / totalTrades) * 100;
        setWinRate(rate);
      } else {
        setWinRate(null);
      }
    } else {
      setWinRate(null);
    }
  }, [winningTrades, losingTrades]);

  // Calculate Profit Factor automatically
  useEffect(() => {
    const profit = parseFloat(totalProfit);
    const loss = parseFloat(totalLoss);

    if (profit >= 0 && loss > 0) {
      const factor = profit / loss;
      setProfitFactor(factor);
    } else if (profit > 0 && (isNaN(loss) || loss === 0)) {
      // If there's profit but no loss, profit factor is infinite
      setProfitFactor(null);
    } else {
      setProfitFactor(null);
    }
  }, [totalProfit, totalLoss]);

  // Reset Risk/Reward & Position Size values
  const resetRiskRewardValues = () => {
    setEntryPrice("");
    setStopLoss("");
    setTarget1("");
    setTarget2("");
    setAccountSize("");
    setRiskPercent("1");
    setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
  };

  // Reset Win Rate Calculator values
  const resetWinRateValues = () => {
    setWinningTrades("");
    setLosingTrades("");
    setWinRate(null);
  };

  // Reset Profit Factor Calculator values
  const resetProfitFactorValues = () => {
    setTotalProfit("");
    setTotalLoss("");
    setProfitFactor(null);
  };

  // Fetch trades for logged in users (with exchange hydration from symbols)
  const fetchTrades = useCallback(async () => {
    if (!user) {
      setTrades([]);
      setTradesLoading(false);
      return;
    }

    try {
      setTradesLoading(true);
      const { data, error } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: false });

      if (error) throw error;

      const baseTrades = (data || []) as any[];
      // Hydrate exchange from symbols table (same as Analytics)
      try {
        const uniqueSymbols = Array.from(new Set(baseTrades.map((t) => t.symbol).filter(Boolean)));
        const uniqueMarkets = Array.from(new Set(baseTrades.map((t) => t.market).filter(Boolean)));
        if (uniqueSymbols.length > 0) {
          let symbolsQuery = supabase
            .from('symbols')
            .select('symbol, market, exchange')
            .in('symbol', uniqueSymbols);
          if (uniqueMarkets.length > 0) {
            symbolsQuery = symbolsQuery.in('market', uniqueMarkets);
          }
          const { data: symbolsData, error: symbolsError } = await symbolsQuery;
          if (!symbolsError && symbolsData) {
            const exchangeByKey = new Map<string, string | null>();
            for (const s of symbolsData as Array<{ symbol: string; market: string; exchange: string | null }>) {
              const key = `${s.market}::${s.symbol}`.toUpperCase();
              if (!exchangeByKey.has(key) || (exchangeByKey.get(key) == null && s.exchange != null)) {
                exchangeByKey.set(key, s.exchange);
              }
            }
            const hydrated = baseTrades.map((t) => {
              const key = `${t.market}::${t.symbol}`.toUpperCase();
              return { ...t, exchange: exchangeByKey.get(key) ?? t.exchange ?? null };
            });
            setTrades(hydrated);
          } else {
            setTrades(baseTrades);
          }
        } else {
          setTrades(baseTrades);
        }
      } catch (hydrateErr) {
        console.warn('Failed to hydrate exchanges from symbols:', hydrateErr);
        setTrades(baseTrades);
      }
    } catch (err: any) {
      console.error('Failed to load trades:', err.message);
      setTrades([]);
    } finally {
      setTradesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades, tradeListRefresh]);

  useMarketDataRefreshInterval(fetchTrades, Boolean(user));

  // Fetch strategies for dashboard widget filters
  useEffect(() => {
    const fetchStrategies = async () => {
      if (!user) {
        setStrategies([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('strategies')
          .select('id, display_name')
          .or(`user_id.eq.${user.id},is_public.eq.true`)
          .eq('is_active', true)
          .order('display_name', { ascending: true });
        if (error) throw error;
        setStrategies(data || []);
      } catch (err: any) {
        console.error('Failed to load strategies:', err);
        setStrategies([]);
      }
    };
    fetchStrategies();
  }, [user]);

  // Fetch accounts for Total Risk Exposure widget filters
  useEffect(() => {
    const fetchDashboardAccounts = async () => {
      if (!user) {
        setDashboardAccounts([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('accounts')
          .select('id, name, platform, currency, initial_balance')
          .eq('user_id', user.id)
          .order('name', { ascending: true });
        if (error) throw error;
        setDashboardAccounts(data || []);
      } catch {
        // initial_balance column may not exist yet — fall back without it
        try {
          const { data, error } = await supabase
            .from('accounts')
            .select('id, name, platform, currency')
            .eq('user_id', user.id)
            .order('name', { ascending: true });
          if (error) throw error;
          setDashboardAccounts((data || []).map((a) => ({ ...a, initial_balance: null })));
        } catch {
          setDashboardAccounts([]);
        }
      }
    };
    fetchDashboardAccounts();
  }, [user]);

  // Fetch exchange rates for dashboard metric widgets (when on home and have trades)
  useEffect(() => {
    if (!user || !trades.length || (location.pathname !== '/app' && location.pathname !== '/app/')) return;
    const closed = trades.filter(
      (t: any) => t.exit_date && t.pnl != null && t.currency
    );
    if (closed.length === 0) return;
    fetchExchangeRates(selectedCurrency, closed);
  }, [user, trades, selectedCurrency, location.pathname, fetchExchangeRates]);

  // Fetch latest exchange rates for open positions + account currencies (for risk % calculation)
  useEffect(() => {
    if (!user || (location.pathname !== '/app' && location.pathname !== '/app/')) return;
    const open = trades.filter((t: any) => !t.exit_date);
    const tradeCurrencies = open.map((t: any) => t.currency || 'USD');
    const accountCurrencies = dashboardAccounts.map((a) => a.currency).filter(Boolean);
    const currencies = Array.from(new Set([...tradeCurrencies, ...accountCurrencies]));
    if (currencies.length === 0) return;
    fetchLatestExchangeRates(selectedCurrency, currencies);
  }, [user, trades, selectedCurrency, location.pathname, dashboardAccounts, fetchLatestExchangeRates]);

  // Redirecting or loading auth - show nothing briefly
  if (!user) {
    if (authLoading) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-500">{t('header.loading', { ns: 'common' })}</p></div>;
    return null;
  }

  // If on all-trades page, render AllTrades component
  if (currentPage === "all-trades") {
    const allTradesProps = { onBack: () => navigate("/app"), setCurrentPage: (page: Parameters<typeof setCurrentPage>[0]) => {
      if (page === "all-trades") navigate("/app/all-trades");
      else if (page === "analytics") navigate("/app/analytics");
      else if (page === "home") navigate("/app");
      else setCurrentPage(page);
    } };
    return (
      <>
        <DashboardLayout onAddTrade={() => navigate("/app/add-trade")} openTradesRefreshTrigger={tradeListRefresh}>
          <AllTrades {...allTradesProps} embedded />
        </DashboardLayout>
        {showLogin && <Login onClose={() => setShowLogin(false)} onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }} />}
        {showRegister && <Register onClose={() => setShowRegister(false)} onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }} />}
      </>
    );
  }

  const wrapWithLayout = (page: React.ReactNode) => (
    <>
      <DashboardLayout onAddTrade={() => navigate("/app/add-trade")} openTradesRefreshTrigger={tradeListRefresh}>
        {page}
      </DashboardLayout>
      {showLogin && <Login onClose={() => setShowLogin(false)} onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }} />}
      {showRegister && <Register onClose={() => setShowRegister(false)} onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }} />}
    </>
  );

  const nav = {
    all: () => navigate("/app/all-trades"),
    insights: () => navigate("/app/insights"),
    addTrade: () => navigate("/app/add-trade"),
    analytics: () => navigate("/app/analytics"),
    accounts: () => navigate("/app/accounts"),
    tools: () => navigate("/app/tools"),
    strategies: () => navigate("/app/strategies"),
    social: () => navigate("/app/social-trade"),
    socialPools: () => navigate("/app/social-trade/trade-pools"),
    socialFollowers: () => navigate("/app/social-trade/followers"),
    community: () => navigate("/app/community"),
    home: () => navigate("/app"),
  };

  const setPage = (p: Parameters<typeof setCurrentPage>[0]) => { if (p === "home") nav.home(); else if (p === "all-trades") nav.all(); else if (p === "insights") nav.insights(); else if (p === "add-trade") nav.addTrade(); else if (p === "create-strategy") navigate("/app/create-strategy"); else if (p === "analytics") nav.analytics(); else if (p === "accounts") nav.accounts(); else if (p === "tools") nav.tools(); else if (p === "strategies") nav.strategies(); else if (p === "social-trade") nav.social(); else if (p === "social-trade-trade-pools") nav.socialPools(); else if (p === "social-trade-followers") nav.socialFollowers(); else if (p === "community") nav.community(); else setCurrentPage(p); };

  // If on analytics page, render Analytics component
  if (currentPage === "analytics") {
    const page = <Analytics onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on insights page, render Insights component
  if (currentPage === "insights") {
    const page = <Insights onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on accounts page, render Accounts component
  if (currentPage === "accounts") {
    const page = <Accounts onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on tools page, render Tools component
  if (currentPage === "tools") {
    const page = <Tools onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on strategies page, render Strategies component
  if (currentPage === "strategies") {
    const page = <Strategies onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on social-trade page, render SocialTrade component
  if (currentPage === "social-trade") {
    const page = <SocialTrade onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // Social Trade sub-pages (only navigated from Social Trade)
  if (currentPage === "social-trade-trade-pools") {
    const page = <TradePools onBack={() => navigate("/app/social-trade")} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  if (currentPage === "social-trade-followers") {
    const page = <Followers onBack={() => navigate("/app/social-trade")} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on account page, render Account component
  if (currentPage === "account") {
    const page = <Account onBack={nav.home} embedded />;
    return wrapWithLayout(page);
  }

  // If on community page, render Community component
  if (currentPage === "community") {
    const page = <Community onBack={nav.home} setCurrentPage={setPage as (p: string) => void} embedded />;
    return wrapWithLayout(page);
  }

  // If on create-strategy page, render CreateStrategy as embedded page
  if (currentPage === "create-strategy") {
    if (!user) return null;
    return wrapWithLayout(<CreateStrategy />);
  }

  // If on add-trade page, render AddTrade as embedded page
  if (currentPage === "add-trade") {
    if (!user) return null;
    const addTradePage = (
      <AddTrade
        embedded
        onClose={() => navigate('/app', { replace: true })}
        onSuccess={() => {
          setTradeListRefresh((p) => p + 1);
          navigate('/app', { replace: true });
        }}
      />
    );
    return wrapWithLayout(addTradePage);
  }

  // If on edit-trade page, fetch trade and render AddTrade (embedded) with trade
  if (currentPage === "edit-trade") {
    if (!user) return null;
    if (!editTradeId) {
      return wrapWithLayout(
        <div className="w-full px-4 sm:px-6 py-6">
          <p className="text-red-600 mb-4">Invalid URL</p>
          <button type="button" onClick={() => navigate('/app', { replace: true })} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            {t('buttons.back', { ns: 'common' })}
          </button>
        </div>
      );
    }
    if (editTradeLoading || (!editTradeData.trade && !editTradeData.error)) {
      return wrapWithLayout(
        <div className="w-full px-4 sm:px-6 py-6 flex items-center justify-center min-h-[200px]">
          <p className="text-gray-500">Loading...</p>
        </div>
      );
    }
    if (editTradeData.error || !editTradeData.trade) {
      return wrapWithLayout(
        <div className="w-full px-4 sm:px-6 py-6">
          <p className="text-red-600 mb-4">{editTradeData.error ?? 'Trade not found'}</p>
          <button
            type="button"
            onClick={() => navigate('/app', { replace: true })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('buttons.back', { ns: 'common' })}
          </button>
        </div>
      );
    }
    const editTradePage = (
      <AddTrade
        embedded
        trade={editTradeData.trade}
        onClose={() => navigate('/app', { replace: true })}
        onSuccess={() => {
          setTradeListRefresh((p) => p + 1);
          navigate('/app', { replace: true });
        }}
      />
    );
    return wrapWithLayout(editTradePage);
  }

  return (
    <>
      <DashboardLayout
        onAddTrade={() => {
          if (user) setQuickAddTradeOpen(true);
          else navigate("/app/add-trade");
        }}
        openTradesRefreshTrigger={tradeListRefresh}
        noPageScroll={!isMobile && (location.pathname === '/app' || location.pathname === '/app/') && !!user && !tradesLoading}
      >
        <div className={`dashboard-page-content ${user && !tradesLoading ? `flex flex-col pb-14 ${isMobile ? 'flex-none min-h-fit overflow-visible' : 'flex-1 min-h-0 overflow-hidden'}` : 'space-y-6 md:space-y-10'}`}>
        {/* Dashboard - 2 columns: left (draggable widgets), right (fixed Feed) */}
        {user && !tradesLoading && (() => {
          const displayEnabled = isEditMode ? pendingEnabled : enabled;
          let enabledOrder: WidgetInstanceId[] = (isEditMode ? pendingOrder : order).filter((id) =>
            displayEnabled.includes(id)
          );
          if (enabledOrder.length === 0) enabledOrder = DEFAULT_DASHBOARD_ORDER;
          // 1x1 widgets always above trade list (grid placement follows this order)
          const is1x1 = (id: WidgetInstanceId) => {
            const baseId = getBaseWidgetId(id);
            const c = DASHBOARD_WIDGETS[baseId as keyof typeof DASHBOARD_WIDGETS];
            return c && c.width === 1 && c.height === 1;
          };
          enabledOrder = [
            ...enabledOrder.filter((id) => is1x1(id)),
            ...enabledOrder.filter((id) => !is1x1(id)),
          ];
          const openPositionsEl = (
            <section className="h-full min-h-0 overflow-hidden flex" aria-label="Open Positions">
              <DashboardWidgetOpenPositions trades={trades} />
            </section>
          );
          const totalPnLEl = (
            <section className="h-full min-h-0 overflow-hidden flex" aria-label="Total P/L %">
              <DashboardWidgetTotalPnL trades={trades} />
            </section>
          );
          const totalPnlOpenEl = (
            <section className="h-full min-h-0 overflow-hidden flex" aria-label="Total P/L Open">
              <DashboardWidgetTotalPnlOpen
                trades={trades}
                selectedCurrency={selectedCurrency}
                latestRates={latestRates}
              />
            </section>
          );
          const tradeListEl = (
            <section
              className="h-full min-h-0 overflow-hidden min-w-0 flex flex-col min-h-[200px]"
              aria-label="Trade list"
            >
              <TradeList
                refreshTrigger={tradeListRefresh}
                isDashboardWidget
                scrollContainerProps={scrollContainerProps}
                onScrollGuard={handleScrollGuard}
              />
            </section>
          );
          const metricWidgetIds = [
            'totalPnl',
            'totalTrades',
            'winRate',
            'profitFactor',
            'avgPnlPercent',
            'weightedPnlPercent',
            'expectancy',
            'averageWin',
            'averageLoss',
            'averageRR',
          ] as const;
          const widgetContent = (id: WidgetInstanceId) => {
            const baseId = getBaseWidgetId(id);
            if (baseId === 'openPositions') return openPositionsEl;
            if (baseId === 'totalPnLPercent') return totalPnLEl;
            if (baseId === 'totalPnlOpen') return totalPnlOpenEl;
            if (baseId === 'totalRiskExposure') return (
              <section className="h-full min-h-0 overflow-hidden flex" aria-label="Total Risk Exposure">
                <DashboardWidgetTotalRisk
                  trades={trades}
                  selectedCurrency={selectedCurrency}
                  latestRates={latestRates}
                  filterSettings={getWidgetSettings(id)}
                  accounts={dashboardAccounts}
                />
              </section>
            );
            if (baseId === 'tradeList') return tradeListEl;
            if (metricWidgetIds.includes(baseId as (typeof metricWidgetIds)[number])) {
              return (
                <section className="h-full min-h-0 min-w-0 overflow-hidden flex flex-col" aria-label={baseId}>
                  <DashboardWidgetMetric
                    trades={trades}
                    filterSettings={getWidgetSettings(id)}
                    metricType={baseId as (typeof metricWidgetIds)[number]}
                    selectedCurrency={selectedCurrency}
                    exchangeRates={exchangeRates}
                    strategies={strategies}
                  />
                </section>
              );
            }
            return null;
          };
          const totalRiskExposureWidgets = enabledOrder
            .filter((id) => getBaseWidgetId(id) === 'totalRiskExposure')
            .map((id) => ({ id, filterSettings: getWidgetSettings(id) }));
          return (
            <div
              className={`flex flex-col md:grid md:grid-cols-[3fr_1fr] md:grid-rows-[minmax(0,1fr)] gap-4 md:gap-6 ${isMobile ? 'flex-none min-h-fit overflow-visible' : 'flex-1 min-h-0 overflow-hidden'}`}
            >
              <div className={`order-2 md:order-1 flex flex-col min-w-0 ${isMobile ? 'flex-none min-h-fit overflow-visible' : 'min-h-0 overflow-y-auto flex-1'}`}>
                <EditableDashboardWidgets
                  enabledOrder={enabledOrder}
                  isEditMode={isEditMode}
                  onReorder={reorderWidgets}
                  onRemove={removeWidget}
                  strategies={strategies}
                  trades={trades}
                  accounts={dashboardAccounts}
                >
                  {(id) => widgetContent(id)}
                </EditableDashboardWidgets>
              </div>
              <div className="order-1 md:order-2 hidden md:flex flex-col min-h-0 overflow-hidden min-w-0 md:h-full">
                <section className="flex flex-col min-h-0 overflow-hidden min-w-0 md:min-h-[200px]">
                  <DashboardFeed
                    openTrades={trades.filter((t) => !t.exit_date)}
                    closedTradesLast2Weeks={trades.filter((t) => {
                      if (!t.exit_date) return false;
                      const exitDate = new Date(t.exit_date);
                      const twoWeeksAgo = new Date();
                      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                      return exitDate >= twoWeeksAgo;
                    })}
                    refreshTrigger={tradeListRefresh}
                    trades={trades}
                    accounts={dashboardAccounts}
                    selectedCurrency={selectedCurrency}
                    latestRates={latestRates}
                    totalRiskExposureWidgets={totalRiskExposureWidgets}
                    onTradeUpdated={() => setTradeListRefresh((n) => n + 1)}
                  />
                </section>
              </div>
            </div>
          );
        })()}

        {/* Loading state when user is logged in but trades are still loading */}
        {user && tradesLoading && (
          <div className="flex-1 flex items-center justify-center min-h-[200px]">
            <p className="text-gray-500">{t('header.loading', { ns: 'common' })}</p>
          </div>
        )}

        {/* Risk Tools Grid - Only show for non-logged in users (Market Hours moved to Tools page) */}
        {!user && (
          <section id="tools" className="grid grid-cols-1 gap-6">
          <div className="space-y-6">
            {/* RR + Position Size */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold">{t('tools:riskReward.title')}</h2>
              <button
                onClick={resetRiskRewardValues}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('tools:riskReward.resetValues')}
              </button>
            </div>
            <div className="space-y-4">
              {/* First row: Account Size and Risk % */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.accountSize')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.accountSizePlaceholder')}
                    value={accountSize}
                    onChange={(e) => setAccountSize(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.riskPercent')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.riskPercentPlaceholder')}
                    value={riskPercent}
                    onChange={(e) => setRiskPercent(e.target.value)}
                  />
                </div>
              </div>

              {/* Second row: Entry and Stop */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.entry')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.entryPlaceholder')}
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.stop')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.stopPlaceholder')}
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                  />
                </div>
              </div>

              {/* Third row: Target 1 and Target 2 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.target1')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.target1Placeholder')}
                    value={target1}
                    onChange={(e) => setTarget1(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.target2')}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:riskReward.target2Placeholder')}
                    value={target2}
                    onChange={(e) => setTarget2(e.target.value)}
                  />
                </div>
              </div>

              {/* Results */}
              {(rrResult.rr1 !== null || rrResult.rr2 !== null || rrResult.suggestedUnits !== null || rrResult.riskDollar !== null || rrResult.unitRiskDollar !== null) && (
                <div className="mt-4 p-4 bg-orange-50 rounded-xl space-y-2">
                  {rrResult.rr1 !== null && (
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{t('tools:riskReward.rr1')}</span>
                      <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr1.toFixed(2)}</span>
                    </div>
                  )}
                  {rrResult.rr2 !== null && (
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{t('tools:riskReward.rr2')}</span>
                      <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr2.toFixed(2)}</span>
                    </div>
                  )}
                  {rrResult.suggestedUnits !== null && (
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{t('tools:riskReward.suggestedUnits')}</span>
                      <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.suggestedUnits.toFixed(4)}</span>
                    </div>
                  )}
                  {rrResult.riskDollar !== null && (
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{t('tools:riskReward.riskDollar')}</span>
                      <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.riskDollar.toFixed(2)}</span>
                    </div>
                  )}
                  {rrResult.unitRiskDollar !== null && (
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{t('tools:riskReward.unitRiskDollar')}</span>
                      <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.unitRiskDollar.toFixed(4)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

            {/* Win Rate Calculator */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold">{t('tools:winRate.title')}</h2>
              <button
                onClick={resetWinRateValues}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('tools:winRate.resetValues')}
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:winRate.winningTrades')}</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:winRate.winningTradesPlaceholder')}
                    value={winningTrades}
                    onChange={(e) => setWinningTrades(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:winRate.losingTrades')}</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:winRate.losingTradesPlaceholder')}
                    value={losingTrades}
                    onChange={(e) => setLosingTrades(e.target.value)}
                  />
                </div>
              </div>

              {/* Result */}
              {winRate !== null && (
                <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('tools:winRate.winRate')}</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-700">{winRate.toFixed(2)}%</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('tools:winRate.winningOf', { winning: parseFloat(winningTrades) || 0, total: parseFloat(winningTrades) + parseFloat(losingTrades) || 0 })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

            {/* Profit Factor Calculator */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold">{t('tools:profitFactor.title')}</h2>
              <button
                onClick={resetProfitFactorValues}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('tools:profitFactor.resetValues')}
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:profitFactor.totalProfit')}</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:profitFactor.totalProfitPlaceholder')}
                    value={totalProfit}
                    onChange={(e) => setTotalProfit(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:profitFactor.totalLoss')}</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('tools:profitFactor.totalLossPlaceholder')}
                    value={totalLoss}
                    onChange={(e) => setTotalLoss(e.target.value)}
                  />
                </div>
              </div>

              {/* Result */}
              {profitFactor !== null && (
                <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('tools:profitFactor.profitFactor')}</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-700">{profitFactor.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('tools:profitFactor.calculation', { profit: parseFloat(totalProfit) || 0, loss: parseFloat(totalLoss) || 0 })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
          </section>
        )}

        </div>

        {/* Footer */}
        <Footer currentPage="home" compact={!!(user && !tradesLoading)} onNavigate={(page) => {
          if (page === "home") navigate("/app");
          else if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "documentation") navigate("/documentation");
          else if (page === "contact") navigate("/contact");
          else if (page === "pricing") window.location.href = "/pricing";
          else if (page === "changelog") window.location.href = "/changelog";
          else setCurrentPage(page);
        }} />
      </DashboardLayout>

      {quickAddTradeOpen && user && (
        <QuickAddTradeModal
          onClose={() => setQuickAddTradeOpen(false)}
          onSuccess={() => {
            setTradeListRefresh((p) => p + 1);
            setQuickAddTradeOpen(false);
          }}
        />
      )}

      {/* Language Preference Modal - blocks new users until they choose language */}
      {showLanguagePreferenceModal && user && (
        <LanguagePreferenceModal
          userId={user.id}
          refreshProfile={refreshProfile}
          onSaved={() => {}}
        />
      )}

      {/* Auth Modals */}
      {showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
      {showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}
    </>
  );
}

export default App;

